<template>
    <el-dialog
      v-model="dialogVisible"
      title="微信扫码登录"
      width="350px"
      :before-close="handleClose"
      @opened="handleDialogOpened"
    >
      <!-- 用 v-show 保持 canvas 挂载 -->
      <div v-show="loading" class="qrcode-loading">
        <el-icon class="is-loading" size="24">
          <Loading />
        </el-icon>
        <span class="loading-text">正在生成二维码...</span>
      </div>
  
      <div v-show="!loading">
        <div class="qrcode-container">
          <canvas ref="qrcodeCanvas" class="qrcode-canvas"></canvas>
        </div>
        <div class="status-message" :class="statusClass">
          {{ statusMessage }}
        </div>
      </div>
  
      <template #footer>
        <el-button @click="closeDialog">取消</el-button>
        <el-button
          v-if="loginState === 'scanned'"
          type="primary"
          @click="handleConfirmLogin"
        >
          确认登录
        </el-button>
      </template>
    </el-dialog>
  </template>
  
  <script setup lang="ts">
  import { ref, watch, nextTick, onUnmounted } from 'vue'
  import QRCode from 'qrcode'
  import axios from 'axios'
  import { ElMessage } from 'element-plus'
  import { Loading } from '@element-plus/icons-vue'
  
  // Props & Emits
  const props = defineProps({
    visible: Boolean,
    apiBaseUrl: {
      type: String,
      default: 'http://localhost:8001/api/auth/wechat'
    }
  })
  // 换成数组形式，不使用泛型
  const emit = defineEmits(['update:visible', 'login-success'])
  
  // Reactive State
  const dialogVisible = ref(false)
  const loading = ref(true)
  const qrcodeCanvas = ref<HTMLCanvasElement | null>(null)
  const currentTicket = ref<string | null>(null)
  // 浏览器环境下 setInterval 返回 number
  const timer = ref<number | null>(null)
  
  type LoginState = 'init' | 'waiting' | 'scanned' | 'confirmed' | 'error'
  const loginState = ref<LoginState>('init')
  const statusMessages: Record<LoginState, string> = {
    init: '正在准备二维码...',
    waiting: '请使用微信扫码登录',
    scanned: '扫码成功，请在手机上确认登录',
    confirmed: '登录成功，正在跳转...',
    error: '登录失败，请重试'
  }
  const statusMessage = ref(statusMessages.init)
  const statusClass = ref('')
  
  // 清理定时器
  function clearPolling() {
    if (timer.value !== null) {
      window.clearInterval(timer.value)
      timer.value = null
    }
  }
  onUnmounted(clearPolling)
  
  // 同步 dialogVisible 与父组件 v-model
  watch(() => props.visible, (v) => {
    dialogVisible.value = v
    if (v) resetState()
    else clearPolling()
  })
  watch(dialogVisible, (v) => {
    if (!v) emit('update:visible', false)
  })
  
  // 重置所有状态
  function resetState() {
    loading.value = true
    loginState.value = 'init'
    statusMessage.value = statusMessages.init
    statusClass.value = ''
    currentTicket.value = null
  }
  
  // 更新登录状态
  function updateStatus(state: LoginState) {
    loginState.value = state
    statusMessage.value = statusMessages[state]
    statusClass.value =
      state === 'error' ? 'status-error' :
      state === 'confirmed' ? 'status-success' : ''
  }
  
  // 对话框打开后生成二维码
  function handleDialogOpened() {
    nextTick(generateQRCode)
  }
  
  // 在关闭前调用
  function handleClose(done: () => void) {
    clearPolling()
    done()
  }
  
  // 关闭对话框
  function closeDialog() {
    clearPolling()
    dialogVisible.value = false
  }
  
  // 生成二维码并开始轮询
  async function generateQRCode() {
    try {
      if (!qrcodeCanvas.value) throw new Error('二维码容器未挂载')
  
      // 设置 canvas 大小
      qrcodeCanvas.value.width = 300
      qrcodeCanvas.value.height = 300
  
      // 请求二维码数据
      const res = await axios.get(`${props.apiBaseUrl}/qrcode`)
      const { ticket, qrcodeUrl } = res.data.data
      currentTicket.value = ticket
  
      // 清空旧绘制
      const ctx = qrcodeCanvas.value.getContext('2d')
      if (ctx) ctx.clearRect(0, 0, 300, 300)
  
      // 打印 URL
      console.log('准备发送请求到二维码 URL:', qrcodeUrl)
  
      // 绘制二维码
      await QRCode.toCanvas(qrcodeCanvas.value, qrcodeUrl, {
        width: 280,
        margin: 2,
        errorCorrectionLevel: 'H',
        color: { dark: '#000000', light: '#ffffff' }
      })
  
      updateStatus('waiting')
      loading.value = false
      startPolling(ticket)
    } catch (err) {
      console.error('生成二维码失败:', err)
      updateStatus('error')
      loading.value = false
      ElMessage.error('生成二维码失败，请重试')
      dialogVisible.value = false
    }
  }
  
  // 轮询扫码/确认状态
  function startPolling(ticket: string) {
    clearPolling()
    timer.value = window.setInterval(async () => {
      try {
        const res = await axios.get(`${props.apiBaseUrl}/check?ticket=${ticket}`)
        const status = res.data.data.status
        if (status === 'SCANNED') {
          updateStatus('scanned')
        } else if (status === 'CONFIRMED') {
          const { token, userInfo } = res.data.data
          handleLoginSuccess(token, userInfo)
        } else if (status === 'EXPIRED') {
          ElMessage.warning('二维码已过期')
          updateStatus('error')
          dialogVisible.value = false
        }
      } catch {
        // 可选：处理请求失败或重试逻辑
      }
    }, 2000)
  }
  
  // 处理登录成功
  function handleLoginSuccess(token: string, userInfo: any) {
    clearPolling()
    updateStatus('confirmed')
    localStorage.setItem('jwt_token', token)
    setTimeout(() => {
      emit('login-success', userInfo)
      dialogVisible.value = false
    }, 1000)
  }
  
  // 用户点击“确认登录”
  async function handleConfirmLogin() {
    if (!currentTicket.value) return
    try {
      await axios.post(`${props.apiBaseUrl}/confirm`, { ticket: currentTicket.value })
      updateStatus('confirmed')
    } catch {
      ElMessage.error('确认登录失败')
      updateStatus('error')
    }
  }
  </script>
  
  <style scoped>
  .qrcode-loading {
    min-height: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }
  .qrcode-loading .el-icon {
    animation: rotating 2s linear infinite;
  }
  @keyframes rotating {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .loading-text {
    color: #666;
    font-size: 14px;
  }
  .qrcode-container {
    display: flex;
    justify-content: center;
    margin: 20px 0;
  }
  .qrcode-canvas {
    display: block;
    margin: 0 auto;
    width: 280px;
    height: 280px;
    border: 1px solid #eee;
  }
  .status-message {
    text-align: center;
    color: #666;
    font-size: 14px;
    margin-top: 10px;
    padding: 8px;
    border-radius: 4px;
  }
  .status-message.status-error {
    color: #f56c6c;
    background-color: #fef0f0;
  }
  .status-message.status-success {
    color: #67c23a;
    background-color: #f0f9eb;
  }
  </style>
  